<script setup lang="ts">
/**
 * /config —— 平台配置(v2 · O6 按画板重排,深色;S-46)。
 *
 * 这一页就是「窗口时长是配置项而不是硬编码」那句话的可见形态:
 * 运营在这里改,改完**当场生效**(订单池与抢单的每一个请求入口都会现读一次),
 * 不需要重启服务 —— 将来国企合作方问「为什么抢不到」时,当场打开这一页就能说清楚。
 *
 * 这四个数各自对应一条决策:
 *   优先窗口时长与开关   L0-05(将来要证明规则一致适用)
 *   空跑费              M-12(取消的第二段要用它)
 *   代办服务费          M-13(2026-10-11 修订:**固定数额**,计入实付、不进佣金基数;
 *                             没有费率形态 —— 费率会让每单的代办费随运费浮动)
 *
 * **上限提示来自 @sea-drone/shared 的 PLATFORM_CONFIG_LIMITS** ——
 * 与 API 的写入口用的是同一份边界。前端自己写一份上限,迟早和后端漂移,
 * 而漂移的表现是「页面说能填,提交被 400 拒」。
 *
 * ═══ 保存按钮为什么单独一行 ═══
 *
 * 四个值是一个 PUT 一次提交的,所以按钮不许挂在任何一组格子下面 ——
 * 挂在「费用」那张卡里会让人以为它只提交费用那两项。
 *
 * 2026-10-11:这一页曾经还有「代办服务费率」与「监管端 IP 白名单」两组控件,
 * 按人裁决整块删掉(decisions.json 的 M-13 / M-07)—— 页面上没有它们,
 * 后端也不再认这三个字段(forbidNonWhitelisted 当场 400)。
 *
 * ═══ 与画板的差异(都记在 design-copy 的 O6 帧里)═══
 *
 * ① 画板「适用范围 全站」「起算时刻 接单释放后」两行**不落**:PlatformConfig 里
 *    没有这两个字段(只有开关 / 时长 / 两笔费用),画出来只能是硬编码两个字 ——
 *    而它们读起来和真值一模一样。两行连同它们的值登记在帧的豁免里。
 * ② 画板「平台佣金率 800 bp(8%)」那一行**不搬进来**:契约里的佣金率挂在**每家
 *    承运商**上(Carrier.commissionRateBp,在承运商页逐家维护),不是平台配置。
 *    要在这儿显示它得先改契约加一个平台级默认率 —— 那是新决策,不是布局。
 * ③ **优先窗口开关**是画板没画、而契约里真有的那一项(L0-05:关掉之后放池单
 *    立刻公开可抢)—— 补件,放在时长旁边。
 * ④ 画板没有提交控件(「本板没有主按钮」):保存照旧是一条整行的 PUT 提交,
 *    理由见上面「保存按钮为什么单独一行」。
 * ⑤ 变更记录卡的数据源只取真事件:eventType = platform_config_updated 的审计行,
 *    行内是「字段 旧值 → 新值」。画板第三行那种「发布计价表」事件在审计枚举里
 *    不存在,不造(登记在帧的豁免里)。
 * ⑥ 行里的操作人显示的是账号 id(actorId 原值,AS-035 的取证口径:这页上的字
 *    要能直接拿去核对日志);画板那三行的「运营 李工 / 系统」是示意数据,不锁。
 */
import { computed, onMounted, ref } from 'vue'
import { NButton, NInput, NSwitch, useMessage } from 'naive-ui'
import { ErrorBanner, PageHeader, PanelCard, type ScopeNote } from '@sea-drone/ui'
import {
  PLATFORM_CONFIG_LIMITS,
  formatCentsYuan,
  formatFenAsYuan,
  formatShanghaiMinute,
  parseWholeNumber,
  parseYuanToFen,
} from '@sea-drone/shared'
import { api, ApiError, type AuditLogItem, type PlatformConfig } from '../api/client'

/** 这一页是运营端的深色页,底座由 S-41 的第三档 tone 给(AppShell 上已挂同一档) */
const TONE = 'ops-dark' as const

const message = useMessage()

const form = ref<PlatformConfig>({
  priorityWindowEnabled: true,
  priorityWindowMinutes: 15,
  deadRunFee: 0,
  agencyFeeDefault: 0,
})
/**
 * 两笔费用的可编辑文本(Y-59):人按「元」填(最多 2 位小数),
 * 加载时由库里的整数分格式化来,保存时精确换回整数分再 PUT。
 * 它们是**表单态**,不是配置值 —— 配置值一直在 form 里,单位始终是分(M-14)。
 */
const deadRunFeeYuan = ref('0')
const agencyFeeYuan = ref('0')
/**
 * 窗口时长的可编辑文本(S-61)。以前是 n-input-number 的 :precision="0",
 * 填 15.6 会被静默四舍五入成 16 —— 运营以为窗口是 15.6 分钟,而库里是 16。
 * 现在按文本收,非整数当场拒绝。
 */
const priorityWindowMinutesText = ref('15')
const lastError = ref<string | null>(null)
const configAudit = ref<AuditLogItem[]>([])
/** 返回体里的 total —— 这个条件下的**全量**条数(服务端计数,不是本卡行数) */
const auditTotal = ref(0)
const busy = ref(false)

const limits = PLATFORM_CONFIG_LIMITS

const NOTES: readonly ScopeNote[] = [
  {
    title: '改完当场生效',
    body:
      '运营在这里改，改完当场生效（订单池与抢单的每一个请求入口都会现读一次），不需要重启服务 —— ' +
      '将来国企合作方问「为什么抢不到」时，当场打开这一页就能说清楚。改完不会退回默认值：库里那一行是唯一真相源。',
    refs: ['L0-05', 'M-12', 'M-13'],
  },
  {
    title: '四个数各对应一条决策',
    body:
      '优先窗口时长与开关 L0-05（将来要证明规则一致适用）；空跑费 M-12（取消的第二段要用它）；' +
      '代办服务费 M-13（固定数额，计入实付，不进佣金基数）。',
    refs: ['L0-05', 'M-12', 'M-13'],
  },
  {
    title: '上限提示是从哪来的',
    body:
      '上限来自 @sea-drone/shared 的 PLATFORM_CONFIG_LIMITS —— 与 API 的写入口用的是同一份边界。' +
      '前端自己写一份上限，迟早和后端漂移，而漂移的表现是「页面说能填，提交被 400 拒」。',
    refs: [],
  },
  {
    title: '为什么不在本地先校验一遍',
    body:
      '服务端会把非法值逐条拒回来（PUT 返回 400，话里点名哪个字段哪里不合法），原句显示在页面顶部的红条里。' +
      '这里不预先做一遍「本地校验」—— 两处判据迟早漂移，而漂移的方向是「页面放过了、服务端拒绝」，' +
      '运营看到一句他自己看不懂的话。',
    refs: ['M-14'],
  },
]

const AUDIT_NOTES: readonly ScopeNote[] = [
  {
    title: '这一列回答什么',
    body: '「那几天为什么自营总是抢不到」这个问题靠这一列回答：谁在什么时候把哪个数从多少改成了多少。',
    refs: [],
  },
  {
    // 事件名原本印在卡片标题上（「配置改动历史（platform_config_updated）」）。
    // 它是**机器名**，摆给用户看的标题里就成了开发草稿；但它是排查时唯一能拿去过滤的值，
    // 丢掉等于从这一页到那批日志的路断了 —— 所以它搬进口径说明，不消失。
    // 审计事件名全仓只在这里与审计明细里出现（见 assumptions.json 的 AS-035）
    title: '这一列的数据是从哪来的',
    body:
      '这一列取的是审计日志里 eventType = platform_config_updated 的那些行。' +
      'eventType 是**机器名**，与库里那一列、与代码里的常量逐字一致 —— 它不翻译，' +
      '因为排查时人要拿它去过滤日志，一个中文译名会在这里多出一次人工反查。' +
      '行里的操作人是账号 id（actorId 原值），与审计明细里的那一列同源。',
    refs: ['M-27'],
  },
]

function show(e: unknown): void {
  const msg =
    e instanceof ApiError
      ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
      : e instanceof Error
        ? e.message
        : String(e)
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

async function refresh(): Promise<void> {
  try {
    form.value = await api.getConfig()
    // 库里的整数分 → 输入框里的元字符串(不四舍五入、不带 ¥ 与千分位,输入框只收纯数字)
    deadRunFeeYuan.value = formatFenAsYuan(form.value.deadRunFee)
    agencyFeeYuan.value = formatFenAsYuan(form.value.agencyFeeDefault)
    priorityWindowMinutesText.value = String(form.value.priorityWindowMinutes)
    const audit = await api.listAuditLogs({ eventType: 'platform_config_updated' })
    configAudit.value = audit.items
    auditTotal.value = audit.total
    lastError.value = null
  } catch (e) {
    show(e)
  }
}

/**
 * 单位换算是格式问题,不是业务校验:解析失败必须当场说出来,
 * **不许静默当 0**(那会把「运营填错了」变成「这项费用免了」,页面上看不出任何异常)。
 */
function rejectFeeField(what: string): void {
  const msg = `${what}:请按「元」填一个非负数,最多 2 位小数(多出的一位不四舍五入)`
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

async function save(): Promise<void> {
  // S-61:窗口时长是整数分钟。解析失败当场拒绝 —— 不四舍五入、不 clamp。
  // 上下限仍交给服务端判(不在本地另写一份范围,两处判据迟早漂移)。
  const priorityWindowMinutes = parseWholeNumber(priorityWindowMinutesText.value)
  if (priorityWindowMinutes === null) {
    const msg = '窗口时长:请填整数分钟;多出的小数不四舍五入'
    lastError.value = msg
    message.error(msg, { duration: 8000 })
    return
  }
  const deadRunFee = parseYuanToFen(deadRunFeeYuan.value)
  if (deadRunFee === null) {
    rejectFeeField('空跑费')
    return
  }
  const agencyFeeDefault = parseYuanToFen(agencyFeeYuan.value)
  if (agencyFeeDefault === null) {
    rejectFeeField('代办服务费')
    return
  }
  busy.value = true
  try {
    // 服务端会把非法值逐条拒回来(PUT 返回 400,话里点名哪个字段哪里不合法)。
    // 这里不预先做一遍「本地校验」—— 两处判据迟早漂移,而漂移的方向是
    // 「页面放过了、服务端拒绝」,运营看到一句他自己看不懂的话
    form.value = await api.updateConfig({
      ...form.value,
      priorityWindowMinutes,
      deadRunFee,
      agencyFeeDefault,
    })
    message.success('已保存并当场生效 —— 订单池与抢单下一个请求就会读到新值')
    await refresh()
  } catch (e) {
    show(e)
  } finally {
    busy.value = false
  }
}

/**
 * 审计详情里那一段 from → to,逐字段写成「字段 旧值 → 新值」(画板那一列的形态)。
 * 字段名保留机器名(AS-035 的取证口径:页面上的字要能拿去核对日志)。
 *
 * 两笔费用在库里是整数分、这里给人看的是元(Y-59):from / to 是数字时套
 * `¥{formatCentsYuan(...)}`。**是数字才换算** —— 不是数字就原样显示,
 * 不猜它是分还是元。其余字段保持原样(它们不是金额)。
 * detail 的形状认不出来时原样显示 JSON —— 不假装它是空的。
 */
function changesOf(detail: Record<string, unknown>): string {
  const changes = detail['changes']
  if (changes === null || typeof changes !== 'object' || Array.isArray(changes)) return JSON.stringify(detail)
  const feeFields = new Set(['deadRunFee', 'agencyFeeDefault'])
  const render = (field: string, raw: unknown): string =>
    feeFields.has(field) && typeof raw === 'number' ? `¥${formatCentsYuan(raw)}` : String(raw)
  const parts = Object.entries(changes as Record<string, { from?: unknown; to?: unknown }>).map(
    ([field, v]) => `${field} ${render(field, v?.from)} → ${render(field, v?.to)}`,
  )
  return parts.length > 0 ? parts.join(' · ') : JSON.stringify(detail)
}

/** 本卡只列最近 50 条(接口默认页大小);服务端的全量条数在标题右侧写着 */
const auditExtra = computed(() =>
  auditTotal.value > configAudit.value.length
    ? `共 ${auditTotal.value} 条（本卡列最近 ${configAudit.value.length} 条）`
    : `共 ${auditTotal.value} 条`,
)

onMounted(() => {
  void refresh()
})
</script>

<template>
  <div class="page">
    <PageHeader :tone="TONE" title="平台配置" :notes="NOTES">
      <template #actions>
        <!-- 画板页头右端那一行:先说明(逐字)、再刷新(幽灵键;这一板的刷新没有图标) -->
        <span class="header-note">改一次全站生效;历史单按快照不变</span>
        <button type="button" class="ghost" @click="refresh">刷新</button>
      </template>
    </PageHeader>

    <ErrorBanner v-if="lastError" :tone="TONE" :message="lastError" title="上一次操作失败" />

    <!-- 画板的双栏:左优先窗口、右费用。两栏各是字段行(名字左、值右) -->
    <div class="panels">
      <PanelCard :tone="TONE" title="优先窗口">
        <p class="panel-desc">承运商独占抢单的时长。窗口内别人看不到这一单。</p>
        <div class="rows">
          <div class="field-row">
            <span class="row-name">窗口时长</span>
            <span class="row-value">
              <n-input v-model:value="priorityWindowMinutesText" class="num-input">
                <template #suffix>分钟</template>
              </n-input>
            </span>
          </div>
          <!-- 开关是画板没画、契约里真有的那一项(L0-05):关掉之后放池单立刻公开可抢 -->
          <div class="field-row">
            <span class="row-name">优先窗口开关</span>
            <span class="row-value">
              <n-switch v-model:value="form.priorityWindowEnabled" />
              <span class="unit">关掉之后放池单立刻公开可抢</span>
            </span>
          </div>
        </div>
        <p class="panel-hint">
          上限 {{ limits.priorityWindowMinutes.max }}（一天）—— 比一天还长的窗口一定是笔误。
          填 0 与关掉开关效果相同：放池单立刻进公开池。
        </p>
      </PanelCard>

      <PanelCard :tone="TONE" title="费用">
        <p class="panel-desc">空跑费在「已接单未取货」取消时扣;改成 0 就是不扣。</p>
        <div class="rows">
          <div class="field-row">
            <span class="row-name">空跑费</span>
            <span class="row-value">
              <n-input v-model:value="deadRunFeeYuan" class="num-input" placeholder="0.00" />
              <span class="unit">元</span>
            </span>
          </div>
          <div class="field-row">
            <span class="row-name">代办服务费</span>
            <span class="row-value">
              <n-input v-model:value="agencyFeeYuan" class="num-input" placeholder="0.00" />
              <span class="unit">元</span>
            </span>
          </div>
        </div>
        <p class="panel-hint">
          上限 ¥{{ formatCentsYuan(limits.feeCents.max) }}（不到 100 万元）—— 防的是少打一个小数点。
          空跑费全额归承运商（M-12）；代办服务费是固定数额（M-13）：实付 = 本值 + 实时计算的运费，
          不随运费浮动，纯运输单恒为 0。
        </p>
      </PanelCard>
    </div>

    <!-- 保存整行:四个值是一个 PUT 一次提交的,按钮不许挂在任何一组格子下面 -->
    <div class="actions">
      <n-button type="primary" :loading="busy" @click="save">保存</n-button>
      <span class="hint-text">上面两组值是一个 PUT 一次提交的 —— 保存会一起写进去</span>
    </div>

    <PanelCard :tone="TONE" title="变更记录" :notes="AUDIT_NOTES">
      <template #extra>
        <span class="hint-text">{{ auditExtra }}</span>
      </template>
      <div v-if="configAudit.length === 0" class="hint-text">还没有任何配置改动</div>
      <div v-else class="records">
        <div v-for="row in configAudit" :key="`${row.occurredAt}-${row.eventType}`" class="record">
          <span class="rec-time">{{ formatShanghaiMinute(row.occurredAt) }}</span>
          <span class="rec-actor">{{ row.actorId ?? '—' }}</span>
          <span class="rec-change">{{ changesOf(row.detail) }}</span>
        </div>
      </div>
    </PanelCard>
  </div>
</template>

<style scoped>
/* 页头与各块之间的纵向节奏:画板是 16 —— 这里由 .page 的 gap 一处给 */
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 页头右端那句口径说明(画板:12px 三级灰,排在刷新键左边) */
.header-note {
  font-size: 12px;
  color: var(--sd-text3);
}

/* 刷新:画板的幽灵键(无底、1px 描边 border-strong、内边距 [8,14]、圆角 sm;本板无图标) */
.ghost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1px solid var(--sd-border-strong);
  border-radius: var(--sd-radius-sm);
  background: transparent;
  color: var(--sd-text2);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}

.ghost:hover {
  color: var(--sd-text1);
  border-color: var(--sd-brand);
}

/* 画板的双栏:左优先窗口、右费用;窄屏落成一条 */
.panels {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

@media (max-width: 1100px) {
  .panels {
    grid-template-columns: 1fr;
  }
}

.panel-desc {
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 1.7;
  color: var(--sd-text3);
}

.panel-hint {
  margin: 12px 0 0;
  font-size: 12px;
  line-height: 1.7;
  color: var(--sd-text3);
}

.rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 字段行:画板是深底一行,名字左、值右 */
.field-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius-sm);
  background: var(--sd-bg);
}

.row-name {
  font-size: 13px;
  color: var(--sd-text2);
}

.row-value {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.unit {
  font-size: 12px;
  color: var(--sd-text3);
}

.num-input {
  width: 160px;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.hint-text {
  font-size: 12px;
  color: var(--sd-text3);
}

/* ── 变更记录 ───────────────────────────────────────── */

.records {
  display: flex;
  flex-direction: column;
}

/* 每条记录一行,行与行之间一条上边线(画板:1140×39,内边距 [10,0]) */
.record {
  display: flex;
  align-items: baseline;
  gap: 16px;
  padding: 10px 0;
  border-top: 1px solid var(--sd-border);
}

.record:first-child {
  border-top: none;
}

.rec-time {
  flex: none;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text3);
}

.rec-actor {
  flex: none;
  min-width: 120px;
  font-family: var(--sd-font-mono);
  font-size: 13px;
  color: var(--sd-text2);
  word-break: break-all;
}

.rec-change {
  font-size: 13px;
  color: var(--sd-text1);
  word-break: break-all;
}
</style>
